import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../App";
import { toast } from "sonner";
import { 
  ChevronLeft, ChevronRight, RefreshCw, Calendar as CalendarIcon, Filter, X, Check, Plus,
  Rocket, Flag, Package, Target, CheckCircle2, ExternalLink, Video, UserPlus
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import { CreateMilestoneDialog } from "../components/calendar/CreateMilestoneDialog";
import { PMFilterPanel } from "../components/calendar/PMFilterPanel";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

// Icon components for event types
const EventIcon = ({ type, isComplete, className = "w-3.5 h-3.5" }) => {
  if (type === "milestone" && isComplete) return <CheckCircle2 className={`${className} text-green-600`} />;
  switch (type) {
    case "program_start": return <Rocket className={`${className} text-blue-600`} />;
    case "program_end": return <Flag className={`${className} text-red-600`} />;
    case "deliverable": return <Package className={`${className} text-purple-600`} />;
    case "milestone": return <Target className={`${className} text-orange-600`} />;
    case "kickoff": return <Video className={`${className} text-teal-600`} />;
    case "meeting": return <Video className={`${className} text-teal-700`} />;
    default: return <Package className={`${className} text-gray-600`} />;
  }
};

const EVENT_COLORS = {
  program_start: { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-200", label: "Program Start" },
  program_end: { bg: "bg-red-100", text: "text-red-700", border: "border-red-200", label: "Deadline" },
  deliverable: { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-200", label: "Deliverable" },
  milestone: { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-200", label: "Milestone" },
  milestone_completed: { bg: "bg-green-100", text: "text-green-700", border: "border-green-200", label: "Completed" },
  kickoff: { bg: "bg-teal-100", text: "text-teal-700", border: "border-teal-200", label: "Kickoff Meeting" },
  meeting: { bg: "bg-teal-100", text: "text-teal-800", border: "border-teal-300", label: "Booked Meeting" }
};

const VIEW_MODES = [
  { id: "month", label: "Month" },
  { id: "week", label: "Week" },
  { id: "day", label: "Day" }
];

// PM Colors for color-coding milestones
const PM_COLORS = [
  { bg: "bg-blue-100", border: "border-blue-400", text: "text-blue-700" },
  { bg: "bg-emerald-100", border: "border-emerald-400", text: "text-emerald-700" },
  { bg: "bg-violet-100", border: "border-violet-400", text: "text-violet-700" },
  { bg: "bg-amber-100", border: "border-amber-400", text: "text-amber-700" },
  { bg: "bg-rose-100", border: "border-rose-400", text: "text-rose-700" },
  { bg: "bg-cyan-100", border: "border-cyan-400", text: "text-cyan-700" },
  { bg: "bg-indigo-100", border: "border-indigo-400", text: "text-indigo-700" },
  { bg: "bg-pink-100", border: "border-pink-400", text: "text-pink-700" },
];

// Get consistent color for a PM based on their ID
function getPMColor(pmId, pmList) {
  if (!pmId || !pmList?.length) return null;
  // Find PM by id - the pmList now contains pm_ids (resource IDs) from unique events
  const pmIndex = pmList.findIndex(p => p.id === pmId);
  if (pmIndex === -1) return null;
  return PM_COLORS[pmIndex % PM_COLORS.length];
}

function EventBadge({ event, compact = false, pmColorMap = {} }) {
  const colorKey = event.type === "milestone" && event.is_complete ? "milestone_completed" : event.type;
  const defaultColors = EVENT_COLORS[colorKey] || EVENT_COLORS.deliverable;
  
  // Use PM color for milestones if PM is assigned
  const pmColor = event.pm_id ? pmColorMap[event.pm_id] : null;
  const colors = pmColor ? { bg: pmColor.bg, text: pmColor.text, border: pmColor.border } : defaultColors;
  
  // Use pm_name from event data (from backend)
  const pmName = event.pm_name || 'Unassigned';

  // Booked meetings are their own thing: they may have no program at all, so the
  // headline is the meeting itself and the link goes to the Meetings hub.
  const isMeeting = event.type === "meeting";
  const target = isMeeting
    ? "/meetings"
    : event.program_id
      ? `/programs/${event.program_id}`
      : "/programs";
  const headline = isMeeting ? event.title : event.program_name;
  const subline = isMeeting
    ? [event.time, event.host_name, event.program_name].filter(Boolean).join(" · ")
    : event.title;

  const tooltipContent = (
    <div className="space-y-2 text-sm p-1">
      <div className="font-semibold">{headline}</div>
      <div className="opacity-80">{subline}</div>
      {isMeeting ? (
        <>
          {event.client_name && (
            <div className="flex items-center gap-2">
              <span className="opacity-60">Client:</span>
              <span>{event.client_name}</span>
            </div>
          )}
          {event.join_url && (
            <a
              href={event.join_url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 text-blue-400 hover:text-blue-300 mt-2 pt-2 border-t border-slate-600"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="w-3 h-3" />
              <span>Join the meeting</span>
            </a>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <span className="opacity-60">PM:</span>
            <span>{pmName}</span>
          </div>
          <Link
            to={target}
            className="flex items-center gap-1 text-blue-400 hover:text-blue-300 mt-2 pt-2 border-t border-slate-600"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="w-3 h-3" />
            <span>View project details</span>
          </Link>
        </>
      )}
    </div>
  );

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to={target}
              className={`flex items-center gap-1 text-xs px-1.5 py-0.5 rounded truncate border ${colors.bg} ${colors.text} ${colors.border}`}
            >
              <EventIcon type={event.type} isComplete={event.is_complete} className="w-3 h-3 shrink-0" />
              <span className="truncate">{headline?.slice(0, 12)}{headline?.length > 12 ? "..." : ""}</span>
            </Link>
          </TooltipTrigger>
          <TooltipContent 
            side="right" 
            className="max-w-[280px] !bg-slate-900 !border !border-slate-700 !text-white shadow-xl"
          >
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={target}
            className={`block p-2 rounded-lg border ${colors.bg} ${colors.text} ${colors.border} hover:opacity-80 transition-opacity`}
          >
            <div className="flex items-start gap-2">
              <EventIcon type={event.type} isComplete={event.is_complete} className="w-4 h-4 shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{headline}</p>
                <p className="text-xs opacity-80 truncate">{subline}</p>
                {event.residence_number && <p className="text-xs opacity-70">Unit: {event.residence_number}</p>}
              </div>
            </div>
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-[280px] bg-slate-900 border border-slate-700 text-white shadow-xl">
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function CalendarView() {
  const [events, setEvents] = useState([]);
  const [milestoneEvents, setMilestoneEvents] = useState([]);
  const [pms, setPms] = useState([]);
  const [pmColorMap, setPmColorMap] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState("");
  const [addingUser, setAddingUser] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("month");
  const [showFilters, setShowFilters] = useState(false);
  const [showMilestoneDialog, setShowMilestoneDialog] = useState(false);
  const [selectedDateForMilestone, setSelectedDateForMilestone] = useState("");
  const [userRole, setUserRole] = useState(null);
  const [defaultPMsSet, setDefaultPMsSet] = useState(false);
  
  // Default PMs to show on calendar - by name (partial match)
  const DEFAULT_PM_NAMES = [
    "Gonzalo",
    "Bernardo", // Berni
    "Sofia",
    "Eugenia",
    "Gabriela",
    "Victoria",
    "Arie"
  ];
  
  // Filters - Default to only milestones per user request
  const [selectedPMs, setSelectedPMs] = useState([]);
  // Milestones plus real booked client meetings. There is no UI to change this
  // yet, so anything not listed here is invisible on the calendar.
  const [selectedEventTypes, setSelectedEventTypes] = useState(["milestone", "meeting"]);

  const fetchData = useCallback(async (isInitial = false) => {
    if (isInitial) setInitialLoading(true);
    try {
      // Build query params for user filter
      const userFilter = selectedPMs.length > 0 ? `?pm_ids=${selectedPMs.join(",")}` : "";
      
      const [eventsRes, milestonesRes, usersRes, userRes, pmResourcesRes] = await Promise.all([
        apiClient.get(`/calendar/events${userFilter}`),
        apiClient.get(`/calendar/milestones${userFilter}`),
        apiClient.get("/users"),
        apiClient.get("/auth/me"),
        apiClient.get("/resources?resource_type=PROJECT_MANAGER")
      ]);
      setEvents(eventsRes.data);
      setMilestoneEvents(milestonesRes.data);
      setAllUsers(usersRes.data || []);
      
      // Build PM list from PM resources (properly linked to users)
      const pmResources = pmResourcesRes.data || [];
      const pmList = pmResources
        .filter(r => r.name && r.user_id) // Only include properly linked PMs
        .map(r => ({ id: r.id, name: r.name, user_id: r.user_id }));
      setPms(pmList);
      
      // Build color map for PMs
      const colorMap = {};
      pmList.forEach((pm, idx) => {
        colorMap[pm.id] = PM_COLORS[idx % PM_COLORS.length];
      });
      setPmColorMap(colorMap);
      
      setUserRole(userRes.data?.role);
    } catch (err) {
      toast.error("Failed to load calendar data");
    }
    setInitialLoading(false);
  }, [selectedPMs]);
  
  // Add user as PM
  const handleAddUserAsPM = async () => {
    if (!selectedUserToAdd) return;
    
    setAddingUser(true);
    try {
      await apiClient.post("/resources/pm-from-user", { user_id: selectedUserToAdd });
      toast.success("User added as PM filter");
      setShowAddUserDialog(false);
      setSelectedUserToAdd("");
      fetchData(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add user as PM");
    }
    setAddingUser(false);
  };
  
  // Get users that aren't already PMs
  const availableUsersToAdd = allUsers.filter(u => {
    // Exclude users that already have a PM resource
    const isAlreadyPM = pms.some(pm => pm.user_id === u.id);
    // Exclude artists
    const isArtist = u.role === "Artist" || u.role === "artist";
    return !isAlreadyPM && !isArtist;
  });

  // Initial load
  useEffect(() => { fetchData(true); }, []);
  
  // Set default PMs once they are loaded
  useEffect(() => {
    if (pms.length > 0 && !defaultPMsSet) {
      const defaultIds = pms
        .filter(pm => DEFAULT_PM_NAMES.some(name => 
          pm.name?.toLowerCase().includes(name.toLowerCase())
        ))
        .map(pm => pm.id);
      
      if (defaultIds.length > 0) {
        setSelectedPMs(defaultIds);
        setDefaultPMsSet(true);
      }
    }
  }, [pms, defaultPMsSet]);
  
  // Refetch when PM filter changes (without showing full loading state)
  useEffect(() => {
    if (!initialLoading && defaultPMsSet) {
      fetchData(false);
    }
  }, [selectedPMs]);

  // Toggle PM filter
  const togglePMFilter = (pmId) => {
    setSelectedPMs(prev => 
      prev.includes(pmId) ? prev.filter(id => id !== pmId) : [...prev, pmId]
    );
  };

  // Handle click on calendar cell to add milestone
  const handleCellClick = (dateStr) => {
    setSelectedDateForMilestone(dateStr);
    setShowMilestoneDialog(true);
  };

  // Check if user can create milestones
  const canCreateMilestones = userRole === "COO" || userRole === "Admin" || userRole === "PM";

  // Date utilities
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const getDaysInMonth = (date) => {
    const y = date.getFullYear();
    const m = date.getMonth();
    const firstDay = new Date(y, m, 1);
    const lastDay = new Date(y, m + 1, 0);
    return { daysInMonth: lastDay.getDate(), startingDayOfWeek: firstDay.getDay(), year: y, month: m };
  };

  const getWeekDays = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    const weekStart = new Date(d.setDate(diff));
    const days = [];
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(weekStart);
      dayDate.setDate(weekStart.getDate() + i);
      days.push(dayDate);
    }
    return days;
  };

  const formatDateStr = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const isToday = (date) => {
    const today = new Date();
    const d = new Date(date);
    return today.toDateString() === d.toDateString();
  };

  // Combine and filter events
  const getAllEvents = useCallback(() => {
    // Events from /calendar/events already includes milestones
    // Filter by event type
    return events.filter(e => {
      if (!selectedEventTypes.includes(e.type)) return false;
      // PM filter - if any PMs selected, filter by assigned PM
      if (selectedPMs.length > 0 && e.pm_id) {
        return selectedPMs.includes(e.pm_id);
      }
      return true;
    });
  }, [events, selectedEventTypes, selectedPMs]);

  const getEventsForDate = (dateStr) => {
    return getAllEvents().filter(e => e.date === dateStr);
  };

  // Navigation
  const prevPeriod = () => {
    const newDate = new Date(currentDate);
    if (viewMode === "month") newDate.setMonth(newDate.getMonth() - 1);
    else if (viewMode === "week") newDate.setDate(newDate.getDate() - 7);
    else newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const nextPeriod = () => {
    const newDate = new Date(currentDate);
    if (viewMode === "month") newDate.setMonth(newDate.getMonth() + 1);
    else if (viewMode === "week") newDate.setDate(newDate.getDate() + 7);
    else newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(new Date());

  // Toggle event type filter
  const toggleEventType = (type) => {
    setSelectedEventTypes(prev => 
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const monthData = getDaysInMonth(currentDate);
  const weekDays = getWeekDays(currentDate);

  // Build month grid
  const calendarDays = [];
  for (let i = 0; i < monthData.startingDayOfWeek; i++) calendarDays.push(null);
  for (let day = 1; day <= monthData.daysInMonth; day++) calendarDays.push(day);

  // Stats for current view
  const allFilteredEvents = getAllEvents();
  const todayStr = new Date().toISOString().split("T")[0];
  const upcomingEvents = allFilteredEvents.filter(e => e.date >= todayStr).slice(0, 15);

  const renderMonthView = () => (
    <div className="grid grid-cols-7 gap-1">
      {calendarDays.map((day, i) => {
        const dateStr = day ? formatDateStr(new Date(year, month, day)) : null;
        const dayEvents = day ? getEventsForDate(dateStr) : [];
        const date = day ? new Date(year, month, day) : null;
        const dayOfWeek = date ? date.getDay() : -1;
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday
        
        return (
          <div
            key={i}
            className={`min-h-[80px] p-1.5 rounded-lg border transition-colors group relative ${
              day 
                ? isToday(date) 
                  ? "border-primary bg-primary/5" 
                  : isWeekend 
                    ? "border-border bg-zinc-100 dark:bg-zinc-800/50" 
                    : "border-border hover:bg-secondary/30"
                : "border-transparent"
            } ${day && canCreateMilestones && !isWeekend ? "cursor-pointer" : ""}`}
            onClick={() => day && canCreateMilestones && !isWeekend && handleCellClick(dateStr)}
          >
            {day && (
              <>
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium ${
                    isToday(date) ? "text-primary" : isWeekend ? "text-muted-foreground" : ""
                  }`}>
                    {day}
                  </span>
                  {canCreateMilestones && !isWeekend && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleCellClick(dateStr); }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-primary/10"
                      title="Add milestone"
                    >
                      <Plus className="w-3 h-3 text-primary" />
                    </button>
                  )}
                </div>
                <div className="space-y-0.5 mt-1">
                  {dayEvents.slice(0, 3).map(event => (
                    <EventBadge key={event.id} event={event} compact pmColorMap={pmColorMap} />
                  ))}
                  {dayEvents.length > 3 && (
                    <p className="text-[10px] text-muted-foreground">+{dayEvents.length - 3} more</p>
                  )}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderWeekView = () => (
    <div className="grid grid-cols-7 gap-2">
      {weekDays.map((date, i) => {
        const dateStr = formatDateStr(date);
        const dayEvents = getEventsForDate(dateStr);
        const dayNum = date.getDate();
        const isCurrentDay = isToday(date);
        
        return (
          <div
            key={i}
            className={`min-h-[300px] p-2 rounded-lg border group ${
              isCurrentDay ? "border-primary bg-primary/5" : "border-border"
            }`}
          >
            <div className={`text-center mb-2 pb-2 border-b flex items-center justify-between ${isCurrentDay ? "border-primary/30" : "border-border"}`}>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">{dayNames[i]}</p>
                <p className={`text-lg font-semibold ${isCurrentDay ? "text-primary" : ""}`}>{dayNum}</p>
              </div>
              {canCreateMilestones && (
                <button
                  onClick={() => handleCellClick(dateStr)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-primary/10"
                  title="Add milestone"
                >
                  <Plus className="w-4 h-4 text-primary" />
                </button>
              )}
            </div>
            <ScrollArea className="h-[250px]">
              <div className="space-y-1.5 pr-2">
                {dayEvents.map(event => (
                  <EventBadge key={event.id} event={event} pmColorMap={pmColorMap} />
                ))}
                {dayEvents.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-4">No events</p>
                )}
              </div>
            </ScrollArea>
          </div>
        );
      })}
    </div>
  );

  const renderDayView = () => {
    const dateStr = formatDateStr(currentDate);
    const dayEvents = getEventsForDate(dateStr);
    
    return (
      <div className="space-y-3">
        <div className="text-center py-4 border-b flex items-center justify-center gap-4">
          <div>
            <p className="text-sm text-muted-foreground">{dayNames[currentDate.getDay()]}</p>
            <p className="text-3xl font-light">{currentDate.getDate()}</p>
            <p className="text-muted-foreground">{monthNames[month]} {year}</p>
          </div>
          {canCreateMilestones && (
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => handleCellClick(dateStr)}
            >
              <Plus className="w-4 h-4 mr-1" /> Add Milestone
            </Button>
          )}
        </div>
        <div className="space-y-2">
          {dayEvents.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No events scheduled</p>
          ) : (
            dayEvents.map(event => (
              <EventBadge key={event.id} event={event} pmColorMap={pmColorMap} />
            ))
          )}
        </div>
      </div>
    );
  };

  const getViewTitle = () => {
    if (viewMode === "day") {
      return `${monthNames[month]} ${currentDate.getDate()}, ${year}`;
    } else if (viewMode === "week") {
      const start = weekDays[0];
      const end = weekDays[6];
      if (start.getMonth() === end.getMonth()) {
        return `${monthNames[start.getMonth()]} ${start.getDate()} - ${end.getDate()}, ${year}`;
      }
      return `${monthNames[start.getMonth()]} ${start.getDate()} - ${monthNames[end.getMonth()]} ${end.getDate()}, ${year}`;
    }
    return `${monthNames[month]} ${year}`;
  };

  return (
    <div data-testid="calendar-view" className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-light" style={{ fontFamily: 'var(--font-sans)' }}>Calendar</h1>
          <p className="text-sm text-muted-foreground mt-1">PM Milestones & Program Events</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => fetchData(false)} className="rounded-full">
            <RefreshCw className="w-4 h-4 mr-1" /> Sync
          </Button>
        </div>
      </div>

      {/* Filters Bar - Above Calendar */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* User Filter (from events data) */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-muted-foreground">Filter by User:</span>
            {pms.map((pm) => {
              const pmColor = pmColorMap[pm.id] || PM_COLORS[0];
              const isSelected = selectedPMs.includes(pm.id);
              return (
                <button
                  key={pm.id}
                  onClick={() => togglePMFilter(pm.id)}
                  className={`px-3 py-1 text-xs font-medium rounded-full border-2 transition-all ${
                    isSelected 
                      ? `${pmColor.bg} ${pmColor.border} ${pmColor.text}` 
                      : 'bg-secondary/50 border-transparent text-muted-foreground hover:bg-secondary'
                  }`}
                >
                  {pm.name || 'Unknown'}
                </button>
              );
            })}
            {/* Add User Button */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setShowAddUserDialog(true)}
                    className="px-2 py-1 text-xs font-medium rounded-full border-2 border-dashed border-muted-foreground/50 text-muted-foreground hover:border-primary hover:text-primary transition-all flex items-center gap-1"
                  >
                    <UserPlus className="w-3 h-3" />
                    Add
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Add a user to the filter list</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {selectedPMs.length > 0 && (
              <button
                onClick={() => setSelectedPMs([])}
                className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
              >
                Clear
              </button>
            )}
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex items-center gap-2">
            {VIEW_MODES.map(mode => (
              <Button
                key={mode.id}
                variant={viewMode === mode.id ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode(mode.id)}
                className="rounded-full"
              >
                {mode.label}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {/* Navigation Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={prevPeriod} className="rounded-full h-8 w-8">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={goToToday} className="text-xs">Today</Button>
          <Button variant="outline" size="icon" onClick={nextPeriod} className="rounded-full h-8 w-8">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <span className="ml-2 text-lg font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>{getViewTitle()}</span>
        </div>
        
        {/* User Color Legend */}
        {pms.length > 0 && (
          <div className="hidden md:flex items-center gap-3 text-xs">
            <span className="text-muted-foreground">User Colors:</span>
            {pms.slice(0, 5).map((pm) => {
              const pmColor = pmColorMap[pm.id] || PM_COLORS[0];
              return (
                <div key={pm.id} className="flex items-center gap-1">
                  <div className={`w-3 h-3 rounded ${pmColor.bg} border ${pmColor.border}`} />
                  <span className="truncate max-w-[80px]">{pm.name || 'User'}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Main Calendar */}
      <Card>
        <CardContent className="pt-6">
          {/* Day Headers for Month/Week view */}
          {viewMode !== "day" && (
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map(day => (
                <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                  {day}
                </div>
              ))}
            </div>
          )}
          
          {viewMode === "month" && renderMonthView()}
          {viewMode === "week" && renderWeekView()}
          {viewMode === "day" && renderDayView()}
        </CardContent>
      </Card>

      {/* Milestones Widget - Below Calendar */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Target className="w-4 h-4" /> 
            {upcomingEvents.length > 0 ? "Upcoming Milestones" : "Recent Milestones"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {(() => {
              // If there are upcoming events, show those
              // Otherwise, show recent/current month milestones
              const eventsToShow = upcomingEvents.length > 0 
                ? upcomingEvents 
                : allFilteredEvents.sort((a, b) => (b.date || '').localeCompare(a.date || '')).slice(0, 10);
              
              if (eventsToShow.length === 0) {
                return (
                  <p className="text-sm text-muted-foreground text-center py-4 col-span-full">
                    No milestones to display
                  </p>
                );
              }
              
              return eventsToShow.slice(0, 10).map(event => {
                const dateStr = event.date || "";
                const eventDate = dateStr ? new Date(dateStr + "T00:00:00") : null;
                const isValidDate = eventDate && !isNaN(eventDate.getTime());
                const pmColor = event.pm_id ? pmColorMap[event.pm_id] : PM_COLORS[0];
                const effectiveColor = pmColor || PM_COLORS[0];
                const isPast = dateStr < todayStr;
                return (
                  <Link
                    key={event.id}
                    to={`/programs/${event.program_id}`}
                    className={`block p-3 rounded-lg border-l-4 ${effectiveColor.border} ${effectiveColor.bg} hover:opacity-80 transition-opacity ${isPast ? 'opacity-70' : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      <EventIcon type={event.type} isComplete={event.is_complete} className="w-4 h-4 shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-medium truncate ${effectiveColor.text}`}>{event.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{event.program_name}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {isValidDate ? eventDate.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "TBD"}
                          {isPast && <span className="ml-1 text-orange-500">(past)</span>}
                        </p>
                      </div>
                    </div>
                  </Link>
                );
              });
            })()}
          </div>
        </CardContent>
      </Card>

      {/* Create Milestone Dialog */}
      <CreateMilestoneDialog
        open={showMilestoneDialog}
        onOpenChange={setShowMilestoneDialog}
        selectedDate={selectedDateForMilestone}
        onMilestoneCreated={fetchData}
      />
      
      {/* Add User as PM Dialog */}
      <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add User to Filter</DialogTitle>
            <DialogDescription>
              Select a user to add to the calendar filter list. This creates a PM resource link for the selected user.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Select User</Label>
              <Select value={selectedUserToAdd} onValueChange={setSelectedUserToAdd}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a user..." />
                </SelectTrigger>
                <SelectContent>
                  {availableUsersToAdd.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground text-center">
                      All users are already added
                    </div>
                  ) : (
                    availableUsersToAdd.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name || user.email} {user.role && `(${user.role})`}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddUserDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddUserAsPM} 
                disabled={!selectedUserToAdd || addingUser}
              >
                {addingUser ? "Adding..." : "Add User"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
